// src/pages/markets/board/card/cardLayout.ts
//
// A card that grows because a score arrived, or because the event has six
// markets instead of two, drags the list under the reader's cursor — every
// minute, in every column. So a card's height must never depend on data that
// keeps arriving: CARD_HEIGHT is the height of every state that has a body to
// fill.
//
// The one exception is a single binary market with no line to draw: reserving
// 67px for a chart that will never exist is the dead space this whole redesign
// set out to remove. Such a card takes CARD_HEIGHT_COMPACT instead. Those two
// are the ONLY heights on the board — a shape that cannot fit the compact one
// keeps the full one rather than inventing a third.
//
// Redesign: the selector row is gone. Market selection now happens by clicking
// a row in the outcome list, and the group toggle is redundant — a column owns
// its group, and the presence of a score in the *other* group is a header
// badge, not a second navigation. The 32px it occupied goes to the body, so
// CARD_HEIGHT is unchanged and the virtualised list never re-measures.
//
// The block heights are set as inline styles rather than sx so the height test
// can read them without a layout engine.

export const HEADER_HEIGHT = 60;
export const BODY_HEIGHT = 200;
export const FOOTER_HEIGHT = 30;

export const CARD_HEIGHT = HEADER_HEIGHT + BODY_HEIGHT + FOOTER_HEIGHT; // 290

export const ROW_HEIGHT = 48;

export const BODY_PADDING_Y = 8;
export const BODY_INNER_HEIGHT = BODY_HEIGHT - 2 * BODY_PADDING_Y; // 184

export const BLOCK_GAP = 6;

// The one-line conviction summary that replaced the score block. It is a row,
// not a block: three numbers, and the detail lives in its hover panel.
export const FIGURE_ROW_HEIGHT = 52;
export const CONVICTION_ROW_HEIGHT = FIGURE_ROW_HEIGHT;

// Everything above it. In dependent mode the distribution bar is drawn inside
// this frame (below the list), so the frame height does not depend on mode.
export const MARKET_BLOCK_HEIGHT =
    BODY_INNER_HEIGHT - CONVICTION_ROW_HEIGHT - BLOCK_GAP; // 126

export const DEPENDENT_BAR_HEIGHT = 10;
export const OUTCOME_ROW_HEIGHT = 20;

// Meta ('New') column: no conviction row, a four-line fact strip instead —
// freshness, who is in, what is on offer, tags. Kept explicit so it stops
// riding on the conviction row's height. The strip carries its own bottom
// padding, so no BLOCK_GAP term here.
export const META_STRIP_HEIGHT = FIGURE_ROW_HEIGHT;
export const META_MARKET_BLOCK_HEIGHT = MARKET_BLOCK_HEIGHT;

// Activity ('Active') column: three lines — the shift pair, the jump chip, and
// volume/trades. Fixed like the meta strip, and reserved even when no activity
// has arrived, so a card does not resize on the minute tick.
export const ACTIVITY_ROW_HEIGHT = 19;
export const ACTIVITY_BLOCK_HEIGHT = FIGURE_ROW_HEIGHT;
export const ACTIVITY_MARKET_BLOCK_HEIGHT =
    BODY_INNER_HEIGHT - ACTIVITY_BLOCK_HEIGHT - BLOCK_GAP; // 126

export const CARD_GAP = 0;
export const CARD_SLOT_HEIGHT = CARD_HEIGHT + CARD_GAP;

export const STALE_AFTER_S = 180;

export const CARD_TEST_IDS = {
    root: 'board-card',
    header: 'board-card-header',
    body: 'board-card-body',
    conviction: 'board-card-conviction',
    activity: 'board-card-activity',
    activityJump: 'board-card-activity-jump',
    activityShift: 'board-card-activity-shift',
    activityVolume: 'board-card-activity-volume',
    activityResolved: 'board-card-activity-resolved',
    activityEmpty: 'board-card-activity-empty',
    footer: 'board-card-footer',
    row: 'board-card-row',
} as const;
// The single-market head: two labels, the split bar, two prices. Fixed so the
// price line below it gets whatever the frame has left.
export const SINGLE_HEAD_HEIGHT = 53;

// A card whose body has no price line to draw gives the space back rather than
// reserving it. One derivation for every body shape, so the card height, the
// body height and the market block always agree.
export type BodyShape = 'score' | 'meta' | 'activity';

// The only height a card may take other than CARD_HEIGHT.
export const CARD_HEIGHT_COMPACT =
    HEADER_HEIGHT +
    2 * BODY_PADDING_Y +
    SINGLE_HEAD_HEIGHT +
    BLOCK_GAP +
    CONVICTION_ROW_HEIGHT +
    FOOTER_HEIGHT; // 217

export interface BodyMetrics {
    /** The market block's frame. */
    block: number;
    /** Everything under it: the conviction row, the fact strip, the activity block. */
    tail: number;
    bodyHeight: number;
    cardHeight: number;
}

// The board has exactly two card heights and no others. A column of cards at
// four different sizes reads as broken, so a shape whose tail will not fit the
// compact card keeps the full one — it snaps back rather than inventing a
// third size. Only the score-shaped body, whose tail is the one-line conviction
// row, is short enough to compact.
export function bodyMetrics(shape: BodyShape, compact: boolean): BodyMetrics {
    const tail = BLOCK_GAP + FIGURE_ROW_HEIGHT;
    const block =
        compact && shape === 'score'
            ? SINGLE_HEAD_HEIGHT
            : BODY_INNER_HEIGHT - tail;
    const bodyHeight = 2 * BODY_PADDING_Y + block + tail;
    return {
        block,
        tail,
        bodyHeight,
        cardHeight: HEADER_HEIGHT + bodyHeight + FOOTER_HEIGHT,
    };
}
